import { Tier, TIERS, type OwnershipLevel } from '@backrs_ui/common-ui';

import { OWNERSHIP_CELL_KEYS, OWNERSHIP_QUESTION_KEYS } from './questionConstants';
import {
  getQuestionByKey,
  getUserResponse,
  didUserSelectOptionWithAnswerByKey,
} from './scoreHelpers';
import { CareerUserResponseSet, CareerQuestion } from '../models/models';

export type { OwnershipLevel };

const LOWER_TIERS: Tier[] = [TIERS.SEMISKILLED, TIERS.TRADE, TIERS.MID];
const UPPER_TIERS: Tier[] = [TIERS.UPPER, TIERS.TOP];

/**
 * Determines the ownership importance level based on the user's career track tier
 * and their lifestyle aspiration quiz answers.
 *
 * Algorithm (from Tom, 4/9/2026):
 * - Critical: lower-tier track + ambitious lifestyle goals, OR upper-tier track + wealth = very important
 * - Less Important: none of the high-aspiration answers selected
 * - Important: default (everyone else)
 */
export function determineOwnershipLevel(
  tier: Tier,
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): OwnershipLevel | null {
  if (questions.length === 0) return null;

  const wealthVeryImportant = checkWealthVeryImportant(responseSet, questions);
  const hasHighAspirations = checkHighAspirations(responseSet, questions, wealthVeryImportant);

  // Condition 1: lower-tier track with ambitious lifestyle goals
  if (LOWER_TIERS.includes(tier) && hasHighAspirations) return 'critical';

  // Condition 2: upper-tier track with wealth = very important
  if (UPPER_TIERS.includes(tier) && wealthVeryImportant) return 'critical';

  // Less Important: no high-aspiration answers at all
  if (!hasHighAspirations) return 'less_important';

  return 'important';
}

function checkWealthVeryImportant(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  return checkAnswer(
    questions,
    responseSet,
    OWNERSHIP_QUESTION_KEYS.BUILDING_WEALTH,
    OWNERSHIP_CELL_KEYS.WEALTH_VERY_IMPORTANT,
  );
}

function checkAnswer(
  questions: CareerQuestion[],
  responseSet: CareerUserResponseSet,
  questionKey: string,
  answerKey: string,
): boolean {
  const question = getQuestionByKey(questions, questionKey);

  if (!question) return false;

  const response = getUserResponse(responseSet, question.id);

  if (!response) return false;

  return didUserSelectOptionWithAnswerByKey({ response, question, answerText: answerKey });
}

function checkHighAspirations(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
  wealthVeryImportant: boolean,
): boolean {
  if (wealthVeryImportant) return true;

  return (
    checkAnswer(
      questions,
      responseSet,
      OWNERSHIP_QUESTION_KEYS.FUTURE_HOME,
      OWNERSHIP_CELL_KEYS.HOME_LARGE,
    ) ||
    checkAnswer(
      questions,
      responseSet,
      OWNERSHIP_QUESTION_KEYS.FUTURE_LIFESTYLE,
      OWNERSHIP_CELL_KEYS.LIFESTYLE_LUXURY,
    )
  );
}
